/**
 * App security: auto-lock, autofill cache, notifications, secure screen.
 */
import { App as CapApp } from "@capacitor/app";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Preferences } from "@capacitor/preferences";
import type { VaultCipher, VaultController } from "@ciphernest/vault-core";

export type SecuritySettings = {
  /** If true, lock the vault UI the moment the app is minimized. Default off. */
  lockOnBackground: boolean;
  /** Minutes of inactivity (foreground or background) before UI lock. 0 = manual only. */
  lockTimeoutMin: number;
  blockScreenshots: boolean;
  notifyOnLock: boolean;
  clipboardClearSec: number; // 0 = never
};

const SETTINGS_KEY = "ciphernest.security";
const SETTINGS_MIGRATE_KEY = "ciphernest.security.v2-lock";
const AUTOFILL_KEY = "ciphernest.autofill.cache";
const PENDING_SAVE_KEY = "ciphernest.autofill.pendingSave";
const BACKGROUND_AT_KEY = "ciphernest.security.backgroundAt";
/** Match Chrome extension: autofill session lasts 14 days until logout / expiry. */
export const AUTOFILL_DAYS = 14;
const DEFAULTS: SecuritySettings = {
  lockOnBackground: false,
  lockTimeoutMin: 5,
  blockScreenshots: true,
  notifyOnLock: true,
  clipboardClearSec: 30,
};

interface VaultSecurityPlugin {
  setSecureFlag(options: { enabled: boolean }): Promise<void>;
  openAutofillSettings(): Promise<void>;
  isAutofillServiceEnabled(): Promise<{ enabled: boolean; supported: boolean }>;
}

const VaultSecurity = registerPlugin<VaultSecurityPlugin>("VaultSecurity");
const AUTOFILL_SNOOZE_KEY = "ciphernest.autofill.promptSnoozeUntil";
const AUTOFILL_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export async function loadSecuritySettings(): Promise<SecuritySettings> {
  const { value } = await Preferences.get({ key: SETTINGS_KEY });
  let parsed: SecuritySettings = { ...DEFAULTS };
  if (value) {
    try {
      parsed = { ...DEFAULTS, ...JSON.parse(value) };
    } catch {
      parsed = { ...DEFAULTS };
    }
  }

  // One-time migration: older builds locked on every minimize.
  const { value: migrated } = await Preferences.get({ key: SETTINGS_MIGRATE_KEY });
  if (!migrated) {
    parsed = {
      ...parsed,
      lockOnBackground: false,
      lockTimeoutMin: parsed.lockTimeoutMin > 0 ? parsed.lockTimeoutMin : 5,
    };
    await Preferences.set({ key: SETTINGS_KEY, value: JSON.stringify(parsed) });
    await Preferences.set({ key: SETTINGS_MIGRATE_KEY, value: "1" });
  }
  return parsed;
}

export async function saveSecuritySettings(s: SecuritySettings) {
  await Preferences.set({ key: SETTINGS_KEY, value: JSON.stringify(s) });
  if (isNativeApp()) {
    try {
      await VaultSecurity.setSecureFlag({ enabled: s.blockScreenshots });
    } catch {
      /* web / missing plugin */
    }
  }
}

export async function isAutofillServiceEnabled(): Promise<{
  enabled: boolean;
  supported: boolean;
}> {
  if (!isNativeApp()) return { enabled: false, supported: false };
  try {
    return await VaultSecurity.isAutofillServiceEnabled();
  } catch {
    return { enabled: false, supported: false };
  }
}

/** Whether we should prompt the user to enable system autofill. */
export async function shouldPromptAutofillSetup(): Promise<boolean> {
  if (!isNativeApp()) return false;
  const { enabled, supported } = await isAutofillServiceEnabled();
  if (!supported || enabled) return false;
  const { value } = await Preferences.get({ key: AUTOFILL_SNOOZE_KEY });
  if (value && Number(value) > Date.now()) return false;
  return true;
}

export async function snoozeAutofillPrompt() {
  await Preferences.set({
    key: AUTOFILL_SNOOZE_KEY,
    value: String(Date.now() + AUTOFILL_SNOOZE_MS),
  });
}

export async function clearAutofillPromptSnooze() {
  await Preferences.remove({ key: AUTOFILL_SNOOZE_KEY });
}

export async function openAutofillSettings() {
  if (!isNativeApp()) {
    throw new Error("Autofill setup is only on the Android app");
  }
  await VaultSecurity.openAutofillSettings();
}

export type AutofillItem = {
  id: string;
  name: string;
  username: string;
  password: string;
  /** Primary URI (first) for older readers. */
  uri: string;
  /** All login URIs for host / androidapp matching. */
  uris: string[];
};

/** Publish unlocked logins for the native AutofillService (14-day session). */
export async function publishAutofillCache(ciphers: VaultCipher[]) {
  const items: AutofillItem[] = [];
  for (const c of ciphers) {
    if (c.type !== 1 || !c.login) continue;
    const uris = (c.login.uris || [])
      .map((u) => (u.uri || "").trim())
      .filter(Boolean);
    const uri = uris[0] || "";
    items.push({
      id: c.id,
      name: c.name,
      username: c.login.username || "",
      password: c.login.password || "",
      uri,
      uris,
    });
  }
  const savedAt = Date.now();
  const expiresAt = savedAt + AUTOFILL_DAYS * 24 * 60 * 60 * 1000;
  await Preferences.set({
    key: AUTOFILL_KEY,
    value: JSON.stringify({
      unlocked: true,
      items,
      savedAt,
      expiresAt,
      updatedAt: savedAt,
    }),
  });
}

export async function clearAutofillCache() {
  await Preferences.set({
    key: AUTOFILL_KEY,
    value: JSON.stringify({
      unlocked: false,
      items: [],
      savedAt: Date.now(),
      expiresAt: 0,
      updatedAt: Date.now(),
    }),
  });
}

export type PendingAutofillSave = {
  name: string;
  username: string;
  password: string;
  uri: string;
  savedAt: number;
};

/** Credentials captured by Android AutofillService SaveInfo. */
export async function readPendingAutofillSave(): Promise<PendingAutofillSave | null> {
  if (!isNativeApp()) return null;
  const { value } = await Preferences.get({ key: PENDING_SAVE_KEY });
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as PendingAutofillSave;
    if (!parsed.password) return null;
    // Skip OTP-like codes the system may have captured
    const p = String(parsed.password).trim();
    if (p.length < 4 || /^\d{4,8}$/.test(p)) {
      await Preferences.remove({ key: PENDING_SAVE_KEY });
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export async function clearPendingAutofillSave() {
  await Preferences.remove({ key: PENDING_SAVE_KEY });
}

async function notifyLocked() {
  const s = await loadSecuritySettings();
  if (!s.notifyOnLock || !isNativeApp()) return;
  try {
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== "granted") return;
    await LocalNotifications.schedule({
      notifications: [
        {
          id: 71001,
          title: "CipherNest locked",
          body: "Vault locked. Autofill still works for up to 14 days — unlock the app to open your vault.",
          schedule: { at: new Date(Date.now() + 500) },
        },
      ],
    });
  } catch {
    /* ignore */
  }
}

export type SecurityBindings = {
  vault: VaultController;
  onLocked: () => void;
  isUnlocked: () => boolean;
};

/**
 * Wire Cap App lifecycle + idle timer. Returns cleanup.
 * Minimize does not lock by default; idle / long background uses lockTimeoutMin.
 * Autofill cache stays until logout or 14-day expiry.
 */
export function startSecurityWatch(bindings: SecurityBindings): () => void {
  let timer: ReturnType<typeof setInterval> | null = null;
  let lastActive = Date.now();
  let appListener: { remove: () => Promise<void> } | null = null;
  let disposed = false;

  const bump = () => {
    lastActive = Date.now();
  };

  /** Lock vault UI; keep Android autofill available. */
  const doLock = async () => {
    if (!bindings.isUnlocked()) return;
    bindings.vault.lock();
    bindings.onLocked();
    await notifyLocked();
  };

  const tick = async () => {
    if (disposed || !bindings.isUnlocked()) return;
    const s = await loadSecuritySettings();
    if (s.lockTimeoutMin <= 0) return;
    const ms = s.lockTimeoutMin * 60_000;
    if (Date.now() - lastActive >= ms) await doLock();
  };

  void (async () => {
    const s = await loadSecuritySettings();
    if (isNativeApp()) {
      try {
        await VaultSecurity.setSecureFlag({ enabled: s.blockScreenshots });
      } catch {
        /* */
      }
    }
    if (isNativeApp()) {
      appListener = await CapApp.addListener("appStateChange", async ({ isActive }) => {
        if (!isActive) {
          await Preferences.set({
            key: BACKGROUND_AT_KEY,
            value: String(Date.now()),
          });
          const conf = await loadSecuritySettings();
          // Optional immediate lock (off by default)
          if (conf.lockOnBackground) await doLock();
          return;
        }

        // Returning to the app: lock if away longer than idle timeout
        const conf = await loadSecuritySettings();
        const { value: bgRaw } = await Preferences.get({ key: BACKGROUND_AT_KEY });
        await Preferences.remove({ key: BACKGROUND_AT_KEY });
        if (
          conf.lockTimeoutMin > 0 &&
          bgRaw &&
          Date.now() - Number(bgRaw) >= conf.lockTimeoutMin * 60_000
        ) {
          await doLock();
          return;
        }
        bump();
      });
    }
    timer = setInterval(() => void tick(), 15_000);
  })();

  if (typeof window !== "undefined") {
    window.addEventListener("pointerdown", bump);
    window.addEventListener("keydown", bump);
  }

  return () => {
    disposed = true;
    if (timer) clearInterval(timer);
    void appListener?.remove();
    if (typeof window !== "undefined") {
      window.removeEventListener("pointerdown", bump);
      window.removeEventListener("keydown", bump);
    }
  };
}
